import { useMemo } from "react";

import { QuestionPreviewData } from "@vericus/cadmus-common";

import { useAppSelector } from "@/data/hooks";
import {
  normalisedTaskBlockPoints,
  selectNodesMeta,
  selectSortedTaskBlocksWithLabel,
  selectTaskBlocksMeta,
} from "@/features/multi-format/task-layout";
import {
  mapFieldInteraction,
  mapFieldResponse,
  mapQuestionType,
} from "@/features/multi-format/utils/map-graphql-to-json-schema-types";
import {
  QuestionBlockFragment,
  QuestionFragment,
  QuestionTreeNodeType,
  QuestionType,
  TaskBlockFragment,
} from "@/generated/graphql";

import { PreviewQuestionBlock } from "./types";

/**
 * Create ordered `SheetBlock` list by merging server fetched TaskBlocks with
 * the local Task Layout slice.
 */
export function usePreviewFromTaskBlocks(
  blocks: TaskBlockFragment[]
): PreviewQuestionBlock[] {
  const nodes = useAppSelector(selectTaskBlocksMeta);
  const setNodes = useAppSelector(selectNodesMeta);

  const layoutBlocks = useAppSelector(selectSortedTaskBlocksWithLabel);

  const layoutBlocksWithPoints = normalisedTaskBlockPoints(layoutBlocks);
  return layoutBlocksWithPoints.flatMap<PreviewQuestionBlock>((layoutBlock) => {
    const taskBlock = blocks.find((block) => block.id === layoutBlock.id);
    if (!taskBlock || layoutBlock.hidden) return [];
    if (taskBlock.question) {
      const node = nodes[layoutBlock.nodeId];
      return [
        {
          id: taskBlock.id,
          question: taskBlock.question,
          longLabel: node?.label ?? "",
          points: layoutBlock.points,
          isChildQuestion: layoutBlock.parentNodeId !== null,
          socraticQuestionerEnabled: layoutBlock.socraticQuestionerEnabled,
        },
      ];
    }
    if (taskBlock.questionSelection) {
      const node = nodes[layoutBlock.nodeId];
      return [
        {
          id: taskBlock.id,
          longLabel: node?.label ?? "",
          points: layoutBlock.points,
          shortLabel: node?.shortLabel ?? null,
          isChildQuestion: false,
          setSelectionId: taskBlock.questionSelection.id,
          sets: taskBlock.questionSelection.sets?.map((set) => ({
            id: set.id,
            longLabel: set.label ?? "",
            points: layoutBlock.points,
            isChildQuestion: false,
            questions: set.questions
              .map((question, index) => {
                const questionSetQuestion = setNodes?.[question.nodeId];
                const questionOrder = (layoutBlock.order ?? 0) + index;
                return {
                  id: question.id,
                  question: question.question,
                  longLabel:
                    questionSetQuestion?.label ?? `Question ${questionOrder}`,
                  shortLabel:
                    questionSetQuestion?.shortLabel ?? `Q${questionOrder}`,
                  points: question.points,
                  isChildQuestion: false,
                };
              })
              .sort((a, b) => (a.shortLabel > b.shortLabel ? 1 : -1)),
          })),
        },
      ];
    }
    return [];
  });
}

/**
 * Re-construct a nested structure of question blocks to be used in the preview.
 */
export function usePreviewFromQuestionBlocks(
  blocks: QuestionBlockFragment[]
): PreviewQuestionBlock[] {
  return useMemo(() => {
    const groupedByParent: Record<string, QuestionBlockFragment[]> = {};

    // Group blocks by their parentNodeId
    blocks.forEach((block) => {
      const parentId = block.parentNodeId || "root";
      if (!groupedByParent[parentId]) {
        groupedByParent[parentId] = [];
      }
      groupedByParent[parentId].push(block);
    });

    // Process the root-level blocks and recursively group sets and questions
    const processBlocks = (parentId: string): PreviewQuestionBlock[] => {
      return (groupedByParent[parentId] || [])
        .sort((a, b) => a.order - b.order)
        .flatMap((block): PreviewQuestionBlock[] => {
          if (block.nodeType === QuestionTreeNodeType.Selection) {
            return [
              {
                id: block.nodeId,
                points: block.points,
                longLabel: block.longLabel ?? "",
                isChildQuestion: false,
                socraticQuestionerEnabled: block.socraticQuestionerEnabled,
                sets: processBlocks(block.nodeId).map((set) => ({
                  ...set,
                  questions: processBlocks(set.id),
                })),
              },
            ];
          }
          if (
            block.question &&
            block.question.questionType === QuestionType.Sub
          ) {
            const childBlocks: PreviewQuestionBlock[] = processBlocks(
              block.nodeId
            ).map((childBlock) => ({
              ...childBlock,
              isChildQuestion: true,
            }));
            return [
              {
                ...block,
                id: block.nodeId,
                isChildQuestion: false,
                longLabel: block.longLabel ?? "",
              },
              ...childBlocks,
            ];
          }
          return [
            {
              ...block,
              id: block.nodeId,
              isChildQuestion: false,
              // Remove ".S_" from the long label if it exists. E.g. "Quetion1.S1" becomes "Question1"
              longLabel: block.longLabel?.split(".S").at(0) ?? "",
            },
          ];
        });
    };

    return processBlocks("root");
  }, [blocks]);
}

export function getQuestionPreviewDataAndPromptDoc(
  question: QuestionFragment,
  points: number | null
) {
  const promptDoc = question.body?.promptDoc ?? null;
  const questionData: QuestionPreviewData = {
    question_type: mapQuestionType(question.questionType),
    max_score: points,
    answer: null,
    references_answer: null,
    field_outcomes: null,
    question_outcome: null,
    prompt_image: question.body?.promptImage || null,
    fields: question.body?.fields
      ? question.body?.fields.map((field) => {
          return {
            identifier: field.identifier,
            response: mapFieldResponse(field.response),
            interaction: mapFieldInteraction(field.interaction),
          };
        })
      : null,
  };
  return {
    promptDoc,
    questionData,
  };
}
